import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ProjectLogActor, ProjectLogRecord } from '@modules/projects/shared/interfaces/project-log.interface';
import { publicFile } from '@shared/helpers/public-file.helper';

export type ProjectLogbookRowAction =
  | { type: 'task'; taskId: number }
  | { type: 'change'; taskId: number }
  | { type: 'project'; projectId: number }
  | { type: 'contact'; contactId: number }
  | { type: 'entry'; projectId: number; entryId: number }
  | { type: 'configurator'; projectId: number; boxId: number }
  | { type: 'team'; projectId: number; employeeId: number };

export interface ProjectLogbookRowView {
  id: number;
  created: string;
  createdLabel: string;
  actorContactId: number | null;
  actorName: string;
  actorAvatarUrl: string | null;
  typeLabel: string;
  description: string;
  action: ProjectLogbookRowAction | null;
  isNavigable: boolean;
  sourceRecord: ProjectLogRecord;
}

export function mapProjectLogRecordToRow(
  record: ProjectLogRecord,
  translateService: TranslateService,
  currentProjectId?: number | null
): ProjectLogbookRowView {
  const action = resolveProjectLogbookRowAction(record, currentProjectId);

  return {
    id: record.id,
    created: record.created,
    createdLabel: formatLogbookCreated(record.created, translateService.currentLang),
    actorContactId: record.actor?.contactId ?? null,
    actorName: getActorName(record.actor),
    actorAvatarUrl: getActorAvatarUrl(record.actor),
    typeLabel: getActivityTypeLabel(record.type, translateService),
    description: getActivityDescription(record, translateService),
    action,
    isNavigable: !!action,
    sourceRecord: record
  };
}

export function resolveProjectLogbookRowAction(
  record: ProjectLogRecord,
  currentProjectId?: number | null
): ProjectLogbookRowAction | null {
  const taskId = findNumericKey(record.data, 'taskId');
  if (taskId) {
    return record.type === 'change_status_changed' ? { type: 'change', taskId } : { type: 'task', taskId };
  }

  const entryId = findNumericKey(record.data, 'entryId');
  if (entryId && currentProjectId) {
    return { type: 'entry', projectId: currentProjectId, entryId };
  }

  const contactId = findNumericKey(record.data, 'contactId');
  if (contactId) {
    return { type: 'contact', contactId };
  }

  const boxId = findNumericKey(record.data, 'boxId');
  if (boxId && currentProjectId) {
    return { type: 'configurator', projectId: currentProjectId, boxId };
  }

  const projectId = findNumericKey(record.data, 'projectId');
  if (projectId) {
    return { type: 'project', projectId };
  }

  const employeeId = findNumericKey(record.data, 'employeeId');
  if (employeeId && currentProjectId) {
    return { type: 'team', projectId: currentProjectId, employeeId };
  }

  return null;
}

function getActorName(actor?: ProjectLogActor | null): string {
  const firstName = String(actor?.userPerson?.firstName || '').trim();
  const lastName = String(actor?.userPerson?.lastName || '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  if (fullName) return fullName;

  const email = String(actor?.userPerson?.email || '').trim();
  return email || '-';
}

function getActorAvatarUrl(actor?: ProjectLogActor | null): string | null {
  const photo = String(actor?.userPerson?.photo || '').trim();
  return photo ? publicFile(photo, 100) : null;
}

function getActivityTypeLabel(type: string, translateService: TranslateService): string {
  const translationKey = `ProjectsDashboard.summary.logbook.types.${type}`;
  const translated = translateService.instant(translationKey);

  if (translated && translated !== translationKey) return translated;

  return humanizeType(type);
}

function getActivityDescription(record: ProjectLogRecord, translateService: TranslateService): string {
  const apiDescription = String(record.description || '').trim();
  if (apiDescription) return apiDescription;

  const translationKey = `ProjectsDashboard.summary.logbook.descriptions.${record.type}`;
  const translated = translateService.instant(translationKey);
  if (translated && translated !== translationKey) return translated;

  return translateService.instant('ProjectsDashboard.summary.logbook.noDescription');
}

function findNumericKey(value: any, keyName: string): number | null {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = findNumericKey(item, keyName);
      if (nested) return nested;
    }

    return null;
  }

  if (typeof value !== 'object') return null;

  if (Object.prototype.hasOwnProperty.call(value, keyName)) {
    const normalized = normalizePositiveNumber(value[keyName]);
    if (normalized) return normalized;
  }

  for (const nestedValue of Object.values(value)) {
    const nested = findNumericKey(nestedValue, keyName);
    if (nested) return nested;
  }

  return null;
}

function normalizePositiveNumber(value: unknown): number | null {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

function humanizeType(type: string): string {
  return String(type || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatLogbookCreated(value: string, language?: string): string {
  if (!value) return '-';

  try {
    return formatDate(value, 'dd.MM.yyyy HH:mm', normalizeLocale(language));
  } catch {
    return value;
  }
}

function normalizeLocale(language?: string): string {
  switch (language) {
    case 'de':
      return 'de-DE';
    case 'pl':
      return 'pl-PL';
    default:
      return 'en-US';
  }
}
